import React, { useEffect, useState } from "react";
import { DatePicker, Form, Input, message } from "antd";
import VitalService from "../../../../../Services/VitalService";
import dayjs from "dayjs";
import { convertUTCDate } from "../../../../../utils/convertionFunc";
import moment from "moment";

dayjs.extend(require("dayjs/plugin/utc"));

const months = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

const vitalsDefaultValues = {
  systolic: 0,
  diastolic: 0,
  spo2: 0,
  heartRate: 0,
  respiratoryRate: 0,
  temperature: 0,
  height: 0,
  weight: 0,
  bloodGlucose: 0,
  bmi: "",
};

const AddVitals = (props) => {
  const [vitalMasterData, setVitalMasterData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [configuredItems, setConfiguredItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState(vitalsDefaultValues);

  useEffect(() => {
    VitalMaster();
    const filtered = Object.entries(props.rpmConfigVitalsList || {})
      .filter(([key, value]) => key !== "status" && value === 1)
      .map((item) => item[0]);
    setConfiguredItems(filtered);
    if (filtered.find((item) => item === "bmi")) {
      // Call and load HI & WE only if bmi configured.
      patientHeightAndWeight();
    }
  }, []);

  const VitalMaster = () => {
    let payload = {
      status: 1,
    };
    VitalService.VitalMasterlist(payload).then((result) => {
      setVitalMasterData(result.data);
    });
  };

  const handleInputChange = (value, fieldName) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [fieldName]: value,
      };

      const height = updated.height;
      const weight = updated.weight;

      if (height && weight && Number(height) !== 0 && Number(weight) !== 0) {
        updated.bmi = calculateBMI(height, weight);
      } else {
        updated.bmi = "";
      }

      return updated;
    });
  };

  const calculateBMI = (height, weight) => {
    const heightInMeters = Number(height) / 100;
    const bmi = Number(weight) / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  const handleVitalSubmit = () => {
    setIsLoading(true);
    const payload = [];
    const currDate = new Date().toUTCString();
    const dateArr = currDate.split(" ");
    const month = months[dateArr[2]];
    const date = dateArr[1];
    const year = dateArr[3];
    const constructedDate = year + "-" + month + "-" + date;
    const time = dateArr[4];
    const constructedDateTime = constructedDate + " " + time;
    const userObj = sessionStorage.getItem("login_result");
    const userData = userObj ? JSON.parse(userObj) : null;

    const hasAnyValue = Object.entries(formData).some(
      ([key, value]) => configuredItems.includes(key) && value
    );
    if (!hasAnyValue) {
      message.warning(
        "Please fill in at least one field before submitting the form."
      );
      setIsLoading(false);
      return;
    }

    if (!selectedDate) {
      message.warning("Please fill in date field before submitting the form.");
      setIsLoading(false);
      return;
    }

    Object.entries(formData).forEach(([fieldName, value]) => {
      // Skip if not configured or no value
      if (!configuredItems.includes(fieldName) || !value) return;

      const vitalRange = vitalMasterData.find(
        (vital) => vital.vitalCode === props.vitalDisplayMap[fieldName]
      );

      if (vitalRange) {
        payload.push({
          account_Id: props.selectedPatient?.account_Id,
          clinic_Id: props.selectedPatient?.clinic_Id,
          consultationId: null,
          createdBy: userData?.user?.userId,
          createdDate: constructedDateTime,
          modifiedBy: userData?.user?.userId,
          modifiedDate: constructedDateTime,
          patientId: props.selectedPatient?.patientId,
          recordedDateTime: selectedDate,
          status: 1,
          units: vitalRange.units,
          value: value,
          vitalCode: vitalRange.vitalCode,
          vitalName: vitalRange.vitalName,
          rpmApplicable: "Y",
        });
      }
    });

    VitalService.AddVitals(payload)
      .then((response) => {
        setIsLoading(false);
        props.handleFetchUpdatedVital();
        message.success("Vitals added successfully.");
        props.onClose();
      })
      .catch((error) => {
        setIsLoading(false);
        message.error("Error occurred. Please try after sometime.!!");
        console.error("Error sending data:", error);
      });
  };

  const convertSelectedDateWithCurrentTime = (
    date,
    format = "YYYY-MM-DD HH:mm:ss"
  ) => {
    if (!date) return null;

    const now = dayjs();

    return dayjs(date)
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .format(format);
  };

  const onDateChange = (date) => {
    if (!date) return;

    const now = moment();

    const dateWithCurrentTime = date
      .hour(now.hour())
      .minute(now.minute())
      .second(now.second())
      .millisecond(now.millisecond());
    return dateWithCurrentTime.toISOString()
  };

  const handleSelectDate = (date) => {
    const formattedDate = convertUTCDate(onDateChange(date), "YYYY-MM-DD HH:mm:ss");
    setSelectedDate(formattedDate);
  };

  const disabledFutureDate = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const patientHeightAndWeight = async () => {
    try {
      const payload = {
        patientId: props.selectedPatient?.patientId,
        vitalCodes: ["HI", "WE"],
        status: 1,
      };

      const latestVitalsResponse = await VitalService.getSpecificVitalData(
        payload
      );

      if (latestVitalsResponse.data) {
        const heightVital = latestVitalsResponse.data.find(
          (v) => v.vitalCode === "HI"
        );
        const weightVital = latestVitalsResponse.data.find(
          (v) => v.vitalCode === "WE"
        );

        if (heightVital) {
          handleInputChange(heightVital.value, "height");
        }

        if (weightVital) {
          handleInputChange(weightVital.value, "weight");
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseModal = () => {
    setFormData(vitalsDefaultValues);
    props.onClose();
  };

  return (
    <div className="mt-6 border rounded-xl  p-1.5">
      <Form layout="vertical">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Form.Item
              name="selectedDate"
              label={<span className="font-medium">Date</span>}
              className="mx-2"
            >
              <DatePicker
                placeholder="Select Date"
                className="w-full h-10"
                format="DD/MM/YYYY"
                onChange={handleSelectDate}
                disabledDate={disabledFutureDate}
              />
            </Form.Item>
          </div>

          {configuredItems.find(
            (item) => item === "systolic" || item === "diastolic"
          ) && (
              <div>
                <Form.Item
                  label={<span className="font-medium">Blood Pressure</span>}
                  name="bloodPressure"
                  className="mx-2"
                >
                  <div className="w-full flex items-center rounded-lg border border-brand-textGray h-10">
                    <Input
                      onChange={(e) =>
                        handleInputChange(e.target.value, "systolic")
                      }
                      maxLength={3}
                      placeholder="SYS"
                      className="outline-none border-none placeholder:text-end"
                    />
                    <span style={{ width: "10%", textAlign: "center" }}>/</span>
                    <Input
                      onChange={(e) =>
                        handleInputChange(e.target.value, "diastolic")
                      }
                      maxLength={3}
                      placeholder="DIA"
                      className="outline-none border-none"
                      suffix={<span style={{ color: "#BEC0C3" }}>mmHg</span>}
                    />
                  </div>
                </Form.Item>
              </div>
            )}
          {configuredItems.find((item) => item === "spo2") && (
            <div>
              <Form.Item
                label={<span className="font-medium">Pulse Ox</span>}
                className="mx-2"
              >
                <Input
                  className="h-10"
                  suffix={<span style={{ color: "#BEC0C3" }}>%</span>}
                  onChange={(e) => handleInputChange(e.target.value, "spo2")}
                ></Input>
              </Form.Item>
            </div>
          )}
          {configuredItems.find((item) => item === "heartRate") && (
            <div>
              <Form.Item
                label={<span className="font-medium">Heart Rate</span>}
                className="mx-2"
              >
                <Input
                  className="h-10"
                  suffix={<span style={{ color: "#BEC0C3" }}>Beats/min</span>}
                  onChange={(e) =>
                    handleInputChange(e.target.value, "heartRate")
                  }
                ></Input>
              </Form.Item>
            </div>
          )}
          {configuredItems.find((item) => item === "respiratoryRate") && (
            <div>
              <Form.Item
                label={<span className="font-medium">Respiration Rate</span>}
                className="mx-2"
              >
                <Input
                  className="h-10"
                  suffix={<span style={{ color: "#BEC0C3" }}>Breaths/min</span>}
                  onChange={(e) =>
                    handleInputChange(e.target.value, "respiratoryRate")
                  }
                ></Input>
              </Form.Item>
            </div>
          )}
          {configuredItems.find((item) => item === "temperature") && (
            <div>
              <Form.Item
                label={<span className="font-medium">Temperature</span>}
                className="mx-2"
              >
                <Input
                  className="h-10"
                  suffix={<span style={{ color: "#BEC0C3" }}>°F</span>}
                  onChange={(e) =>
                    handleInputChange(e.target.value, "temperature")
                  }
                ></Input>
              </Form.Item>
            </div>
          )}
        </div>
      </Form>

      <Form layout="vertical" disabled={isLoading}>
        <div className="grid grid-cols-3 gap-2">
          {configuredItems.find(
            (item) => item === "height" || item === "bmi"
          ) && (
              <div>
                <Form.Item
                  label={<span className="font-medium">Height</span>}
                  className="mx-2"
                >
                  <Input
                    className="h-10"
                    value={formData.height || ""}
                    suffix={<span style={{ color: "#BEC0C3" }}>Cm</span>}
                    onChange={(e) => handleInputChange(e.target.value, "height")}
                  ></Input>
                </Form.Item>
              </div>
            )}
          {configuredItems.find(
            (item) => item === "weight" || item === "bmi"
          ) && (
              <div>
                <Form.Item
                  label={<span className="font-medium">Weight</span>}
                  className="mx-2"
                >
                  <Input
                    className="h-10"
                    value={formData.weight || ""}
                    suffix={<span style={{ color: "#BEC0C3" }}>Kg</span>}
                    onChange={(e) => handleInputChange(e.target.value, "weight")}
                  ></Input>
                </Form.Item>
              </div>
            )}
          {configuredItems.find((item) => item === "bmi") && (
            <>
              <div>
                <Form.Item
                  label={<span className="font-medium">BMI</span>}
                  className="mx-2"
                >
                  <Input
                    className="h-10"
                    disabled
                    suffix={
                      <span style={{ color: "#BEC0C3" }}>
                        Kg/m<sup>2</sup>
                      </span>
                    }
                    value={formData.bmi}
                  ></Input>
                </Form.Item>
              </div>
            </>
          )}
        </div>
      </Form>
      <div className="gutter-row" span={24}>
        <div className="flex items-center w-full justify-end my-5">
          <button
            disabled={isLoading}
            className="bg-white rounded border border-brand-primaryColor text-brand-primaryColor text-sm px-6 py-1.5"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            className="bg-brand-primaryColor rounded px-5 py-1.5 text-white text-sm ml-3 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleVitalSubmit}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVitals;
